"use client"

import React from "react"
import { Flex, HStack, Icon, Input, Text, Link, useToast } from "@chakra-ui/react"
import { constant } from "common/constant"
import { globalStyles } from "common/theme/styles"
import "common/assets/css/fileUpload.css"
import { useState, useCallback, useRef } from "react"
import { removeTimestamp } from "services/Crm/utils/calcUtils"

const FileUpload = ({
  label,
  name,
  icon: IconComponent,
  iconColor,
  selectedFile,
  title,
  handleFileChange,
  inputId,
  onChange,
  onBlur,
  onInputCapture,
  errorText,
  type = "file",
  borderColor,
  accept,
  isRequired = false,
  ...props
}) => {
  const colors = globalStyles.colors
  const toast = useToast()
  const [previewUrl, setPreviewUrl] = useState(null)
  const [currentFileObject, setCurrentFileObject] = useState(null) // Store File object internally
  const fileInputRef = useRef(null)

  const getFullFileName = (fileName) => {
    if (!fileName) return constant.noFileChoosen
    return fileName.replace(/^(Selected|Current):\s*/, "")
  }

// IMPROVED: Better display of selected file names
const getDisplayFileName = (fileName) => {
  if (!fileName) return constant.noFileChoosen

  const cleanFileName = getFullFileName(fileName)

  // If it's a long filename, truncate it nicely
  if (cleanFileName.length >= 30) {
    const extension = cleanFileName.split(".").pop()
    const nameWithoutExt = cleanFileName.substring(0, cleanFileName.lastIndexOf("."))
    const truncatedName = nameWithoutExt.substring(0, 20)
    return `${truncatedName}...${extension}`
  }

  return cleanFileName
}

  // SMART: Enhanced file change handler that captures File object
  const handleSmartFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0]

      // Store the File object for preview
      if (file) {
        setCurrentFileObject(file)
        console.log("File captured for preview:", {
          name: file.name,
          size: file.size,
          type: file.type,
        })
      } else {
        setCurrentFileObject(null)
      }

      // Call the original handler
      if (handleFileChange) {
        handleFileChange(event)
      } else if (onChange) {
        onChange(event)
      }
    },
    [handleFileChange, onChange],
  )

  // NEW: Handle file preview using captured File object
  const handlePreviewFile = useCallback(async () => {
    if (!currentFileObject || !(currentFileObject instanceof File)) {
      toast({
        title: "No file to preview",
        description: "Please select a file first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      const fileType = currentFileObject.type
      const fileName = currentFileObject.name
      const fileExtension = fileName.split(".").pop()?.toLowerCase()

      console.log("Previewing file:", {
        name: fileName,
        type: fileType,
        size: currentFileObject.size,
        extension: fileExtension,
      })

      // Clean up previous preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }

      // Create object URL for preview
      const objectUrl = URL.createObjectURL(currentFileObject)
      setPreviewUrl(objectUrl)

      // Handle different file types
      if (fileType.startsWith("image/")) {
        // Preview images in new tab
        const newWindow = window.open("", "_blank")
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head>
                <title>Preview: ${fileName}</title>
                <style>
                  body { 
                    margin: 0; 
                    padding: 20px; 
                    background: #f5f5f5; 
                    display: flex; 
                    justify-content: center; 
                    align-items: center; 
                    min-height: 100vh;
                    font-family: Arial, sans-serif;
                  }
                  img { 
                    max-width: 100%; 
                    max-height: 90vh; 
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    border-radius: 8px;
                  }
                  .info {
                    position: fixed;
                    top: 10px;
                    left: 10px;
                    background: white;
                    padding: 10px;
                    border-radius: 4px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    font-size: 12px;
                    color: #666;
                  }
                </style>
              </head>
              <body>
                <div class="info">
                  <strong>${fileName}</strong><br>
                  Size: ${(currentFileObject.size / 1024).toFixed(1)} KB<br>
                  Type: ${fileType}
                </div>
                <img src="${objectUrl}" alt="${fileName}" onload="document.title='Preview: ${fileName}'" />
              </body>
            </html>
          `)
        }
      } else if (fileType === "application/pdf") {
        // Preview PDFs in new tab
        window.open(objectUrl, "_blank")
      } else if (fileType.startsWith("text/") || ["txt", "csv", "json", "xml", "log"].includes(fileExtension)) {
        // Preview text files
        const reader = new FileReader()
        reader.onload = (e) => {
          const content = e.target.result
          const newWindow = window.open("", "_blank")
          if (newWindow) {
            newWindow.document.write(`
              <html>
                <head>
                  <title>Preview: ${fileName}</title>
                  <style>
                    body { 
                      font-family: 'Courier New', monospace; 
                      padding: 20px; 
                      background: #f8f9fa; 
                      line-height: 1.5;
                    }
                    .header {
                      background: white;
                      padding: 15px;
                      border-radius: 8px;
                      margin-bottom: 20px;
                      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    }
                    .content {
                      background: white;
                      padding: 20px;
                      border-radius: 8px;
                      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                      white-space: pre-wrap;
                      word-wrap: break-word;
                      max-height: 70vh;
                      overflow-y: auto;
                    }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <h3>${fileName}</h3>
                    <p>Size: ${(currentFileObject.size / 1024).toFixed(1)} KB | Type: ${fileType}</p>
                  </div>
                  <div class="content">${content}</div>
                </body>
              </html>
            `)
          }
        }
        reader.readAsText(currentFileObject)
      } else if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(fileExtension)) {
        // For Office documents, show info and download option
        toast({
          title: "Preview not available",
          description: `${fileExtension.toUpperCase()} files cannot be previewed. Click OK to download and open externally.`,
          status: "info",
          duration: 5000,
          isClosable: true,
        })

        // Trigger download
        const link = document.createElement("a")
        link.href = objectUrl
        link.download = fileName
        link.style.display = "none"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        // For other file types, show file info
        const newWindow = window.open("", "_blank")
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head>
                <title>File Info: ${fileName}</title>
                <style>
                  body { 
                    font-family: Arial, sans-serif; 
                    padding: 40px; 
                    background: #f8f9fa; 
                  }
                  .card {
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    max-width: 500px;
                    margin: 0 auto;
                  }
                  .file-icon {
                    font-size: 48px;
                    text-align: center;
                    margin-bottom: 20px;
                  }
                  .file-info {
                    text-align: center;
                  }
                  .download-btn {
                    background: #007bff;
                    color: white;
                    padding: 12px 24px;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 16px;
                    margin-top: 20px;
                  }
                  .download-btn:hover {
                    background: #0056b3;
                  }
                </style>
              </head>
              <body>
                <div class="card">
                  <div class="file-icon">📄</div>
                  <div class="file-info">
                    <h2>${fileName}</h2>
                    <p><strong>Size:</strong> ${(currentFileObject.size / 1024).toFixed(1)} KB</p>
                    <p><strong>Type:</strong> ${fileType || "Unknown"}</p>
                    <p><strong>Last Modified:</strong> ${new Date(currentFileObject.lastModified).toLocaleString()}</p>
                    <button class="download-btn" onclick="downloadFile()">Download File</button>
                  </div>
                </div>
                <script>
                  function downloadFile() {
                    const link = document.createElement('a');
                    link.href = '${objectUrl}';
                    link.download = '${fileName}';
                    link.click();
                  }
                </script>
              </body>
            </html>
          `)
        }
      }

      toast({
        title: "File preview opened",
        description: `${fileName} opened in new tab`,
        status: "success",
        duration: 2000,
        isClosable: true,
      })
    } catch (error) {
      console.error("Error previewing file:", error)
      toast({
        title: "Preview failed",
        description: "Unable to preview this file",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }, [currentFileObject, previewUrl, toast])

  // Clean up object URL when component unmounts or file changes
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  // Check if we have a file to preview (either fresh upload or existing)
  const hasPreviewableFile = currentFileObject && selectedFile && !selectedFile.includes("Selected: undefined")

  return (
    <>
      {label && (
        <Text className="labels">
          {label} {isRequired && <span className="astrick">*</span>}
        </Text>
      )}
      <Flex className="attachMentInput" alignItems="center" gap="13px">
        <HStack
          as="label"
          htmlFor={inputId}
          className="attachMentInput-label"
          border="1px solid"
          borderColor={borderColor || colors.borderColor}
          cursor={props.isDisabled ? "not-allowed" : "pointer"}
          opacity={props.isDisabled ? 0.6 : 1}
          pointerEvents={props.isDisabled ? "none" : "auto"}
        >
          {IconComponent && <Icon as={IconComponent} color={iconColor || colors.cta["100"]} />}
          <Text>{title}</Text>
          <Input
            ref={fileInputRef}
            name={name}
            id={inputId}
            type={type}
            accept={accept}
            display="none"
            onChange={handleSmartFileChange} // Use our enhanced handler
            onBlur={onBlur}
            onInputCapture={onInputCapture}
            disabled={props.isDisabled}
            {...props}
          />
        </HStack>

        {/* SMART: Show clickable preview link when file is available */}
        {hasPreviewableFile ? (
          <Link
            className="fileName"
            fontSize="sm"
            mt={1}
            flex="1"
            minW={0}
            maxW="340px"
            display="block"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            color={errorText ? colors.errorText : colors.cta["100"]}
            cursor="pointer"
            textDecoration="underline"
            _hover={{
              color: colors.cta["200"],
              textDecoration: "none",
            }}
            onClick={handlePreviewFile}
            title={`Click to preview: ${getFullFileName(selectedFile)}`}
          >
            📄 {getDisplayFileName(selectedFile)}
          </Link>
        ) : (
          <Text
            className="fileName"
            fontSize="sm"
            mt={1}
            flex="1"
            minW={0}
            maxW="340px"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            color={errorText ? colors.errorText : "inherit"}
            title={getFullFileName(selectedFile)}
          >
            {getDisplayFileName(selectedFile)}
          </Text>
        )}
      </Flex>
      {errorText && (
        <Text color={colors.errorText} className="errorText">
          {errorText}
        </Text>
      )}
    </>
  )
}

export default FileUpload
